/**
 * Componente de Notificaciones Push - C&M Publicaciones
 * Botón para activar/desactivar notificaciones push
 */

import React, { useState, useEffect } from 'react';
import { Bell, BellOff, BellRing, Loader2, CheckCircle, AlertCircle, X } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { authAPI } from '../services/api';
import {
  isPushSupported,
  getNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  getPushStatus,
  sendTestNotification
} from '../services/pushNotifications';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const PushNotificationButton = ({ variant = 'default', showLabel = true, className = '' }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    checkPushStatus();
  }, [token]);

  const checkPushStatus = async () => {
    if (!isPushSupported()) {
      setIsSupported(false);
      return;
    }

    if (getNotificationPermission() === 'denied') {
      setPermissionDenied(true);
      return;
    }

    if (token) {
      try {
        const status = await getPushStatus(token);
        setIsSubscribed(status.subscribed);
      } catch (error) {
        console.error('Error checking push status:', error);
      }
    }
  };

  const handleTogglePush = async () => {
    if (!token) {
      toast.error('Debes iniciar sesión para activar notificaciones');
      return;
    }

    setLoading(true);

    try {
      if (isSubscribed) {
        // Desactivar
        await unsubscribeFromPush(token);
        setIsSubscribed(false);
        toast.success('Notificaciones push desactivadas');
      } else {
        // Activar
        await subscribeToPush(token);
        setIsSubscribed(true);
        toast.success('¡Notificaciones push activadas! Recibirás alertas de nuevos documentos.');
        
        // Enviar notificación de prueba
        setTimeout(async () => {
          try {
            await sendTestNotification(token);
          } catch (e) {
            // Silenciar error de prueba
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error toggling push:', error);
      
      if (error.message.includes('denegado')) {
        setPermissionDenied(true);
        toast.error('Debes permitir las notificaciones en tu navegador');
      } else {
        toast.error(error.message || 'Error al cambiar estado de notificaciones');
      }
    } finally {
      setLoading(false);
    }
  };

  // No mostrar si no está soportado
  if (!isSupported) {
    return null;
  }

  // Si el permiso fue denegado permanentemente
  if (permissionDenied) {
    if (variant === 'minimal') return null;
    
    return (
      <Button
        variant="ghost"
        size="sm"
        disabled
        className={`text-gray-400 cursor-not-allowed ${className}`}
        title="Notificaciones bloqueadas - Habilítalas en la configuración del navegador"
      >
        <BellOff className="h-4 w-4" />
        {showLabel && <span className="ml-2 text-xs">Bloqueadas</span>}
      </Button>
    );
  }

  // Si ya está suscrito, no mostrar nada (según la solicitud del usuario)
  if (isSubscribed && variant === 'hidden-when-active') {
    return null;
  }

  return (
    <Button
      variant={isSubscribed ? 'ghost' : variant === 'prominent' ? 'default' : 'outline'}
      size="sm"
      onClick={handleTogglePush}
      disabled={loading}
      className={`${isSubscribed ? 'text-emerald-600 hover:text-emerald-700' : ''} ${className}`}
      title={isSubscribed ? 'Desactivar notificaciones push' : 'Activar notificaciones push'}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : isSubscribed ? (
        <BellRing className="h-4 w-4" />
      ) : (
        <Bell className="h-4 w-4" />
      )}
      {showLabel && (
        <span className="ml-2 text-sm">
          {loading ? 'Procesando...' : isSubscribed ? 'Push Activo' : 'Activar Push'}
        </span>
      )}
    </Button>
  );
};

/**
 * Modal de bienvenida para activar notificaciones push
 * Se muestra en el primer login
 */
export const PushNotificationModal = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleActivate = async () => {
    setLoading(true);
    setError(null);

    try {
      await subscribeToPush(token);
      setSuccess(true);
      
      // Marcar en localStorage que ya mostró el modal
      localStorage.setItem('push_modal_shown', 'true');
      
      // Enviar notificación de prueba
      setTimeout(async () => {
        try {
          await sendTestNotification(token);
        } catch (e) {
          // Silenciar
        }
      }, 1000);

      // Cerrar después de 2 segundos
      setTimeout(() => {
        onClose();
      }, 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('push_modal_shown', 'true');
    onClose();
  };

  const handleNoMostrarMas = async () => {
    // Persistir en backend (sobrevive a limpiezas de caché, otros dispositivos)
    try {
      await authAPI.setPreferenciasPush(true);
    } catch (e) {
      // silencioso — al menos queda el localStorage
    }
    localStorage.setItem('push_modal_shown', 'true');
    toast.success('No volveremos a mostrar este aviso. Podés activar las notificaciones en cualquier momento desde tu panel.');
    onClose();
  };

  if (!isPushSupported()) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-teal-600" />
            ¿Activar Notificaciones Push?
          </DialogTitle>
        </DialogHeader>

        {success ? (
          <div className="py-6 text-center">
            <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-800">¡Notificaciones Activadas!</p>
            <p className="text-sm text-gray-600 mt-2">
              Recibirás una notificación de prueba en unos segundos.
            </p>
          </div>
        ) : (
          <>
            <DialogDescription className="space-y-4">
              <div className="bg-teal-50 rounded-lg p-4 border border-teal-100">
                <p className="font-medium text-teal-800 mb-2">¿Qué son las notificaciones push?</p>
                <p className="text-sm text-teal-700">
                  Son alertas que aparecen en tu pantalla, incluso cuando no tienes la plataforma abierta. 
                  Te permiten estar siempre al día con la documentación técnica.
                </p>
              </div>

              <div className="space-y-2">
                <p className="font-medium text-gray-700">Te notificaremos cuando:</p>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li className="flex items-center gap-2">
                    <span className="text-emerald-500">✓</span>
                    Se agregue un nuevo documento a tu biblioteca
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-500">✓</span>
                    Un documento existente tenga una nueva revisión
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-amber-500">✓</span>
                    Haya directivas o alertas importantes
                  </li>
                </ul>
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg flex items-center gap-2 text-sm">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}
            </DialogDescription>

            <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-2 sm:justify-between items-stretch sm:items-center" data-testid="push-modal-footer">
              <Button
                variant="link"
                size="sm"
                onClick={handleNoMostrarMas}
                disabled={loading}
                className="text-gray-500 hover:text-red-600 px-0 sm:px-3 underline-offset-2 hover:no-underline"
                data-testid="push-no-mostrar-mas-btn"
              >
                <X className="h-3.5 w-3.5 mr-1" />
                No volver a mostrarme esto
              </Button>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  disabled={loading}
                  data-testid="push-ahora-no-btn"
                >
                  Ahora no
                </Button>
                <Button
                  onClick={handleActivate}
                  disabled={loading}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="push-activar-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Activando...
                    </>
                  ) : (
                    <>
                      <Bell className="h-4 w-4 mr-2" />
                      Sí, activar
                    </>
                  )}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PushNotificationButton;
